<template>
    <div class="component-customer-product-carousel">
        <div class="carousel">
            <div
                ref="container"
                class="slides"
            >
                <div
                    v-for="(slide, index) in data.products"
                    :key="index"
                    ref="elements"
                    class="slide"
                >
                    <CustomProductCarouselCard
                        :data="slide"
                        :sizes="[
                            'calc(100vw - 80px)',
                            [640, 'calc((100vw - 80px) / 2)'],
                            [1024, 'calc((100vw - 80px) / 3)'],
                            [1140, 'calc((100vw - 80px) / 4)'],
                            [1360, '340px'],
                            [1440, '359px'],
                        ]"
                        @select="select(index)"
                    />
                </div>
            </div>
        </div>

        <ButtonRound
            :disabled="isAtStart"
            :aria-label="t('catalogue.carousel.button_prev')"
            class="button-round left"
            icon="angle-left"
            @click="previous"
        />

        <ButtonRound
            :disabled="isAtEnd"
            :aria-label="t('catalogue.carousel.button_next')"
            class="button-round right"
            icon="angle-right"
            @click="next"
        />
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import type { ComponentCustomProductCarouselFragment } from '@/graphql/api'
import { useCarousel } from '@/components/layout/carousels/base/useCarouselNew'

import ButtonRound from '@/components/ui/buttons/ButtonRound.vue'
import CustomProductCarouselCard from '@/components/cms/component/carousel/card/CustomProductCarouselCard.vue'

const { data } = defineProps<{
    data: ComponentCustomProductCarouselFragment
}>()

const { t } = useI18n()

const { container, elements, previous, next, isAtStart, isAtEnd } = useCarousel(
    computed(() => data.products.length)
)

const select = (index: number) => {
    elements.value?.[index]?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
    })
}
</script>

<style lang="scss" scoped>
.component-customer-product-carousel {
    position: relative;
    margin: auto;
    z-index: 10;
    max-width: 1440px;

    @include hover() {
        z-index: 11;
    }

    .carousel {
        width: 100%;
        height: 390px;

        @include min-width(1024) {
            height: 280px;
        }

        @include min-width(1360) {
            padding: 0;
        }

        .slides {
            position: relative;
            // width: 100%;
            display: flex;
            overflow-y: hidden;
            overflow-x: scroll;
            scroll-snap-type: x mandatory;
            scroll-padding: 0 40px;
            padding: 0 40px;
            -ms-overflow-style: none; /* IE and Edge */
            scrollbar-width: none; /* Firefox */

            &::-webkit-scrollbar {
                display: none;
            }

            &::after {
                content: '';
                padding-right: 40px;

                @include min-width(1024) {
                    padding-right: 0;
                }
            }

            @include min-width(1360) {
                padding: 0;
                scroll-padding: 0;
            }

            .slide {
                position: relative;
                user-select: none;
                scroll-snap-align: start;
                flex: 1 0 100%;
                margin-right: 5px;

                @include min-width(640) {
                    flex: 1 0 50%;
                    scroll-snap-align: start;
                }

                @include min-width(1024) {
                    flex: 1 0 33.33%;
                }

                @include min-width(1140) {
                    margin: 0;
                    max-width: 340px;
                    flex: 1 0 25%;
                }

                @include min-width(1360) {
                    max-width: 359px;
                }

                .product-card {
                    margin-right: -1px;
                }
            }
        }
    }

    .button-round {
        position: absolute;
        z-index: 3;
        top: calc(50% - 25px);

        &.disabled {
            opacity: 0;
        }

        &.left {
            left: 15px;
        }

        &.right {
            right: 15px;
        }

        @include min-width(1600) {
            &.left {
                left: -20px;
            }

            &.right {
                right: -20px;
            }
        }
    }
}
</style>
